import { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Alert, AlertTitle, Button, Snackbar, Stack } from "@mui/material";
import { BASE_URL } from "../config/config";

// Popups for things that happen to you while you are in the app: a bill was
// raised, a payment was confirmed, a move-out is waiting on dues. The server
// already stores each of these as a notification (and emails it); this is what
// makes them appear on screen instead of waiting in the bell menu.
//
// It polls, because the notification list is a plain REST resource. Anything
// already shown is remembered per user so a refresh does not replay it.

const POLL_MS = 30 * 1000;
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // older unread items stay in the bell only
const MAX_AT_ONCE = 3;
const SEEN_LIMIT = 200;

const SEVERITY = {
  bill_approved: "success",
  payment_received: "success",
  move_out_completed: "success",
  property_approved: "success",
  bill_rejected: "error",
  payment_overdue: "error",
  dues_pending: "warning",
  payment_reminder: "warning",
  bill_raised: "info",
  move_out: "info",
};

const seenKey = (userId) => `ots_seen_notifications_${userId}`;

const readSeen = (userId) => {
  try {
    return new Set(JSON.parse(localStorage.getItem(seenKey(userId)) || "[]"));
  } catch (e) {
    return new Set();
  }
};

const writeSeen = (userId, seen) => {
  try {
    localStorage.setItem(seenKey(userId), JSON.stringify([...seen].slice(-SEEN_LIMIT)));
  } catch (e) {
    // Storage can be unavailable (private window); the popup just may repeat.
  }
};

export default function LiveNotifications() {
  const navigate = useNavigate();
  const userId = useSelector((state) => state.validateUser?.UserValidate?.data?.user?._id);
  const [queue, setQueue] = useState([]);
  const seenRef = useRef(new Set());

  const poll = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await axios.get(
        `${BASE_URL}/notifications/${userId}?unreadOnly=true&limit=10`
      );
      const items = data?.data?.notifications || [];
      const fresh = items.filter((n) => {
        if (seenRef.current.has(n._id)) return false;
        const age = Date.now() - new Date(n.createdAt || n.date || 0).getTime();
        return age <= MAX_AGE_MS;
      });

      // Everything unread is now "seen" — old items should not pop up later —
      // but only the newest few are worth interrupting the user for.
      items.forEach((n) => seenRef.current.add(n._id));
      writeSeen(userId, seenRef.current);

      if (fresh.length) {
        setQueue((q) => [...q, ...fresh.slice(0, MAX_AT_ONCE)]);
      }
    } catch (e) {
      // A missed poll is harmless; the next one will catch up.
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;
    seenRef.current = readSeen(userId);
    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => clearInterval(timer);
  }, [userId, poll]);

  const close = (id, markRead = true) => {
    setQueue((q) => q.filter((n) => n._id !== id));
    if (markRead) {
      axios.post(`${BASE_URL}/notifications/mark-read/${id}`).catch(() => {});
    }
  };

  if (!queue.length) return null;

  return (
    <Stack
      spacing={1}
      sx={{ position: "fixed", top: 76, right: 16, zIndex: 2000, maxWidth: 380 }}
    >
      {queue.slice(0, MAX_AT_ONCE).map((n) => (
        <Snackbar
          key={n._id}
          open
          autoHideDuration={12000}
          onClose={(_, reason) => reason !== "clickaway" && close(n._id, false)}
          sx={{ position: "static", transform: "none" }}
        >
          <Alert
            severity={SEVERITY[n.type] || "info"}
            variant="filled"
            onClose={() => close(n._id)}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  close(n._id);
                  navigate("/notifications");
                }}
              >
                View
              </Button>
            }
            sx={{ width: "100%", alignItems: "flex-start" }}
          >
            <AlertTitle>{n.title}</AlertTitle>
            {n.message}
          </Alert>
        </Snackbar>
      ))}
    </Stack>
  );
}
